/**
 * Find a category in the tree by ID.
 * @param {Array} categories - Root categories (or any subtree)
 * @param {number} categoryId - ID to find
 * @returns {Object|null} - Category object or null
 */
export function findCategoryInTree(categories, categoryId) {
  if (!categories || categoryId == null || categoryId === "") return null;

  for (const cat of categories) {
    if (Number(cat.id) === Number(categoryId)) {
      return cat;
    }
    if (cat.children && cat.children.length > 0) {
      const found = findCategoryInTree(cat.children, categoryId);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Get the parent category ID and all its descendant IDs from the tree.
 * @param {Array} categories - Root categories tree
 * @param {number|null} parentCategoryId - Parent category ID
 * @returns {Array<number>} - [parentCategoryId, ...descendant ids], or [] if null/empty; if not found returns [parentCategoryId]
 */
/**
 * Collects category-attribute ids from structured `/categories/attributes` payload
 * (root attributes and nested attributes under option values).
 */
export function collectStructuredCategoryAttributeIds(structuredPayload) {
  const ids = [];
  const roots = Array.isArray(structuredPayload)
    ? structuredPayload
    : structuredPayload?.categoryAttributesDTO;
  if (!Array.isArray(roots)) {
    return ids;
  }

  const walkAttr = (attr) => {
    if (!attr || attr.id == null) return;
    ids.push(Number(attr.id));
    const values = attr.categoryAttributesValueDTO || [];
    for (const val of values) {
      if (val?.categoryAttributesDTO) {
        walkAttr(val.categoryAttributesDTO);
      }
    }
  };

  for (const attr of roots) {
    walkAttr(attr);
  }
  return ids;
}

export function getAllCategoryIdsFromTree(categories, parentCategoryId) {
  if (parentCategoryId == null || parentCategoryId === undefined) {
    return [];
  }
  if (!categories || categories.length === 0) {
    return [parentCategoryId];
  }

  const node = findCategoryInTree(categories, parentCategoryId);
  if (!node) {
    return [parentCategoryId];
  }

  const ids = [parentCategoryId];
  const collectChildIds = (parent) => {
    if (!parent.children || parent.children.length === 0) return;
    for (const child of parent.children) {
      ids.push(child.id);
      collectChildIds(child);
    }
  };
  collectChildIds(node);
  return ids;
}
